import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, RefreshCw, BarChart3 } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { spendOptimizationService } from '../../services/spendOptimizationService';

const CreateAnalysis = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisFormData, setAnalysisFormData] = useState({
    startDate: '',
    endDate: '',
    currency: 'USD',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAnalysisFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAnalysisSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        period: {
          startDate: analysisFormData.startDate,
          endDate: analysisFormData.endDate,
        },
        filters: {
          currency: analysisFormData.currency,
        },
      };
      const response = await spendOptimizationService.analyzeSpend(payload);
      if (response.success) {
        success('Spend analysis completed');
        // Let user navigate back cleanly, ideally we'd pass the new analysis ID back
        // to the optimization dashboard via state, but sticking to standard navigation is fine.
        setTimeout(() => {
          navigate('/finance/optimization');
        }, 1000);
      } else {
        showError('Analysis failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      showError(error.message || 'Failed to run spend analysis');
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/finance/optimization');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)',
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Cancel and go back',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Run Spend Analysis"
        description="Select criteria to analyze your organization's spend patterns and identify savings opportunities."
        actions={headerActions}
      />

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleAnalysisSubmit}>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <BarChart3 size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Analysis Parameters
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}
                >
                  {/* Start Date */}
                  <div>
                    <label style={labelStyle}>Start Date *</label>
                    <input
                      type="date"
                      name="startDate"
                      value={analysisFormData.startDate}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label style={labelStyle}>End Date *</label>
                    <input
                      type="date"
                      name="endDate"
                      value={analysisFormData.endDate}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Currency */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Currency</label>
                    <input
                      type="text"
                      name="currency"
                      value={analysisFormData.currency}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="e.g. USD, EUR, GBP"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Running Analysis...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Run Analysis
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateAnalysis;
